import React from "react";
import { styled } from "styled-components";
import { tokens } from "./tokens";

/**
 * Maximise / restore for one card of the tractor page.
 *
 * The page is a set of windows — the record, the tickets, the map — sharing one
 * column. Any of them can take the whole column for a while: a long record on a
 * laptop, a hundred tickets on anything. One control, drawn the same on every
 * card, so learning it once is enough; the label names the card because three
 * identical icons on one screen are otherwise indistinguishable to a screen
 * reader. Escape restores too (handled by the page).
 */
interface WindowToggleProps {
  /** What the card is, lower-case, for the accessible name. */
  label: string;
  expanded: boolean;
  onToggle: () => void;
}

const WindowToggle: React.FC<WindowToggleProps> = ({
  label,
  expanded,
  onToggle,
}) => {
  const name = expanded ? "Restore layout" : `Maximize ${label}`;
  return (
    <Button
      type="button"
      aria-label={name}
      title={expanded ? `${name} (Esc)` : name}
      aria-pressed={expanded}
      onClick={onToggle}
    >
      {expanded ? (
        <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
          <path
            d="M6.5 2v4.5H2M9.5 14V9.5H14M2 9.5h4.5V14M14 6.5H9.5V2"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : (
        <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
          <path
            d="M2 6.5V2h4.5M14 9.5V14H9.5M9.5 2H14v4.5M6.5 14H2V9.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </Button>
  );
};

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  flex: none;
  padding: 0;
  border: 1px solid ${tokens.line};
  border-radius: 6px;
  background: ${tokens.white};
  color: ${tokens.slate};
  cursor: pointer;
  transition:
    color 0.12s,
    border-color 0.12s,
    background 0.12s;

  &:hover {
    color: ${tokens.ink};
    border-color: ${tokens.slate};
  }
  &[aria-pressed="true"] {
    color: ${tokens.blue};
    border-color: ${tokens.blue};
    background: rgba(0, 105, 255, 0.06);
  }
  &:focus-visible {
    outline: 2px solid ${tokens.blue};
    outline-offset: 2px;
  }
`;

export default WindowToggle;
